import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import GoogleLogo from "../../../images/google-logo.svg";
import passwordIcon from "../../../images/password-toggle-icon.svg";
import passwordClosedIcon from "../../../images/password-toggle-icon-closed.svg";
import { emailValidation } from "../../../helpers/validations";
import TextBox from "../../Common/TextBox";
import { resetModalemailOnly, SIGNUP } from "../../../constants/onBoarding";
import _ from "lodash";
import { userLogin, verifyEmail } from "../../../services/v2/signupService";
import { updateUserDetailsToStorage } from "../../../helpers/authHelpers";
import { useOAuthLogin } from "../../../hooks/useOAuthLogin";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import { useNavigate } from "react-router-dom";
import useIsMobile from "@/hooks/useIsMobile";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "@/reducers/modals";
import useLoading from "@/hooks/useLoading";

const LOADING_KEY = "login";

const LoginModal = (props) => {

  const {
    onHide,
    handleAgentOnboardingModal,
    onLoginSuccess,
    handleResetPwdModal,
  } = props;

  const { visible } = useSelector((state) => state.modals.loginModal);
  const { startLoading, stopLoading } = useLoading();

  const show = visible || props?.show;

  const { isGitchaDefaultWebsite, mls } = useCurrentWebsite();

  const initialFormData = {
    email: "",
    pwd: "",
  };

  const initialErrors = {
    emailError: "",
    pwdError: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState(initialErrors);
  const [isShowPassword, setPasswordShow] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [inputsReadOnly, setInputsReadOnly] = useState(true);

  const onTextChange = (e) => {
    setLoginError(null);
    const value = e.target.value;
    setFormData((formData) => ({ ...formData, [e.target.name]: value }));
  };

  const handleOAuthSuccess = (response, provider) => {
    stopLoading(LOADING_KEY);
    updateUserDetailsToStorage(response, provider);
    handleCloseModal?.();
    onLoginSuccess?.();
  };

  const handleOAuthLoginWithLoading = (provider) => {
    startLoading(LOADING_KEY);
    handleOAuthLogin(provider);
  };

  const { handleOAuthLogin } = useOAuthLogin({ onAuthSuccess: handleOAuthSuccess });

  const isMobile = useIsMobile();

  const checkEmailExist = async (email) => {
    const userEmail = { Email: email };
    return await verifyEmail(userEmail).then((response) => {
      return response?.result;
    });
  };

  const validate = async () => {
    setFormErrors(initialErrors);
    let isValid = true;
    const email = _.get(formData, "email", null).trim();

    if (!emailValidation(email)) {
      setFormErrors((formErrors) => ({
        ...formErrors,
        emailError: "Invalid Email",
      }));
      isValid = false;
    } else {
      const res = await checkEmailExist(email);
      const userExist = _.get(res, "UserExists", false);
      const userVerified = _.get(res, "IsVerified", false);
      const isDeleted = _.get(res, "IsDeleted", false);

      if (userExist && userVerified && !isDeleted) {
        isValid = true;
      } else {
        setFormErrors((formErrors) => ({
          ...formErrors,
          emailError: "Invalid user",
        }));
        isValid = false;
      }
    }

    return isValid;
  };

  const handleSubmit = async () => {
    startLoading(LOADING_KEY);
    try {
      const isValid = await validate();

      if (isValid) {
        const reqPayload = {
          email: _.get(formData, "email", ""),
          password: _.get(formData, "pwd", ""),
          mls
        };

        await userLogin(reqPayload)
          .then((response) => {
            if (_.get(response, "errorCode", "") === 0) {
              updateUserDetailsToStorage(response);
              handleCloseModal?.();
              onLoginSuccess?.();
            } else {
              setLoginError(_.get(response, "message", ""));
            }
          })
          .catch((err) => setLoginError(err?.response?.data?.message));
      }
    } finally {
      stopLoading(LOADING_KEY);
    }
  };

  const handleClearFormState = () => {
    setFormData(initialFormData);
    setFormErrors(initialErrors);
    setLoginError(null);
    setPasswordShow(false);
  };

  const dispatch = useDispatch();

  const handleCloseModal = () => {
    handleClearFormState?.()
    onHide();
    if (visible) {
      dispatch(closeModal({ name: "loginModal" }));
    }
  };

  useEffect(() => {
    if (!show) {
      handleClearFormState();
      setInputsReadOnly(true);
    }
  }, [show]);

  const handleToggleToSignup = () => {
    handleAgentOnboardingModal?.({ showPopup: true, userAction: SIGNUP })
  }

  const envName = mls !== 'default' ? mls.charAt(0).toUpperCase() + mls.slice(1) : 'Gitcha'

  return (
    <Modal
      show={show}
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      className={`${isMobile ? '' : 'modal-width-548'}${props?.className ? ` ${props.className}` : ''}`}
      fullscreen={isMobile}
      onHide={handleCloseModal}
    >
      <Modal.Body className="signup-modal-main" style={isMobile ? { justifyContent: 'center', display: 'flex', flexDirection: 'column' } : {}}>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={handleCloseModal}
        ></button>

        <div className="d-flex align-items-center justify-content-between">
          <h2 className="text-center mb-0 m-auto">Login to {envName}</h2>
        </div>

        <div>
          <p className="text-center mb-32 pt-32">
            By continuing, you agree to {envName.concat(`'s`)}{" "}
            <a role="button" target="_blank" href="terms-of-service">
              Terms
            </a>{" "}
            and
            <a role="button" target="_blank" href="privacy-policy">
              {" "}
              Privacy Policy.
            </a>
          </p>

          <Button
            variant="outline-primary"
            className="btn outline-btn w-100 mb-16"
            onClick={() => handleOAuthLoginWithLoading("google")}
          >
            <img src={GoogleLogo} alt="Google Login" className="me-2" />
            <span>Continue with Google</span>
          </Button>

          <div className="segment-divider mb-16">OR</div>
        </div>

        <div className="textbox-wrap mb-2 mb-sm-2 position-relative">
          <label className="form-label">Email</label>
          <TextBox
            name="email"
            type="text"
            tabIndex="1"
            autoComplete="email"
            readonly={inputsReadOnly}
            onFocus={() => setInputsReadOnly(false)}
            errorMessage={_.get(formErrors, "emailError", "")}
            value={_.get(formData, "email", "")}
            placeholder="yourname@email.com"
            className={
              _.get(formErrors, "emailError", null)
                ? "form-control validation-txtfld"
                : "form-control"
            }
            onChange={(e) => {
              onTextChange(e);
            }}
          />
        </div>

        <div className="textbox-wrap mb-0 mb-sm-0 position-relative">
          <label className="form-label">Password</label>
          <TextBox
            name="pwd"
            type={isShowPassword ? "text" : "password"}
            tabIndex="2"
            autoComplete="current-password"
            readonly={inputsReadOnly}
            onFocus={() => setInputsReadOnly(false)}
            errorMessage={_.get(formErrors, "pwdError", "")}
            value={_.get(formData, "pwd", "")}
            placeholder="Password"
            errorClass="error-msg height-inherit"
            className={
              _.get(formErrors, "pwdError", null)
                ? "pad-right-40 validation-txtfld"
                : "pad-right-40"
            }
            onChange={(e) => {
              onTextChange(e);
            }}
          />
          {loginError ? (
            <div className="error-msg height-inherit">{loginError}</div>
          ) : (
            <div className="error-msg height-inherit">
              {loginError && loginError !== "" && (
                <span className="error-msg">{loginError}</span>
              )}
            </div>
          )}

          <Button
            variant=""
            className="btn btn-pwd-toggle"
            onClick={() => setPasswordShow((state) => !state)}
          >
            <img
              src={isShowPassword ? passwordIcon : passwordClosedIcon}
              alt="pwdicon"
            />
          </Button>
        </div>

        <p className="text-start mb-32">
          <a
            href={void 0}
            role="button"
            onClick={() =>
              handleResetPwdModal({
                showResetModal: true,
                resetPwdModalType: resetModalemailOnly,
              })
            }
          >
            Forgot your password?
          </a>
        </p>

        <Button
          variant="primary"
          tabIndex="4"
          className="w-100 mb-3"
          disabled={!formData.email && !formData.pwd}
          onClick={() => handleSubmit()}
        >
          Continue
        </Button>

        {isGitchaDefaultWebsite && (
          <p className="text-center">
            Don't have an account yet?{" "}
            <a href={void 0} role="button" onClick={handleToggleToSignup}>
              Sign up here
            </a>
          </p>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default LoginModal;
